import React, { useCallback, useState } from "react";

import { Chip, Popover, Stack, styled } from "@mui/material";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { defaultDataroomFilesFilters } from "constants/datarooms";
import { useFormatDateHook } from "hooks";
import { useUserPermissions } from "store";
import { DataroomFilesFilters, UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";

interface Props {
  filters: DataroomFilesFilters;
  selectedDocType?: string;
  selectedAccName?: string;
  setFilters: (callback: (filters: DataroomFilesFilters) => DataroomFilesFilters) => void;
  disabledMultiItems?: Array<PluralFiltersKeys>;
}

export type PluralFiltersKeys = keyof Pick<DataroomFilesFilters, "accounts" | "deals" | "altiusDeals" | "docTypes">;

const filterNames: Record<PluralFiltersKeys, string> = {
  accounts: "Account Name",
  deals: "Deals",
  docTypes: "Document Type",
  altiusDeals: "Altius Deals"
};

const StyledChip = styled(Chip)(() => ({
  height: 24,
  fontSize: "12px",
  fontWeight: 400
}));

export const FilterChips: React.FC<Props> = ({ filters, setFilters, disabledMultiItems = [] }) => {
  const { createdAtDate, syncAtDate, effectiveAtDate, uploadedAtDate, synced, notSynced, ...multiFilters } = filters;

  const [selectedFilters, setSelectedFilters] = useState<{
    key: PluralFiltersKeys;
    anchor: HTMLElement;
  } | null>(null);

  const format = useFormatDateHook();
  const userPermissions = useUserPermissions();
  const isAdminPermission =
    userPermissions?.["cloud_integrations"] && userPermissions["cloud_integrations"] === UserPermissionType.ADMIN;

  const handleDeleteField = useCallback(
    (field: PluralFiltersKeys) => {
      setFilters((prevState) => ({
        ...prevState,
        [field]: defaultDataroomFilesFilters[field]
      }));
    },
    [setFilters]
  );

  const handleDeleteFieldValue = useCallback(
    (field: PluralFiltersKeys, value: string) => {
      setFilters((prevState) => ({
        ...prevState,
        [field]: prevState[field].filter((item) => item !== value)
      }));
    },
    [setFilters]
  );

  const createdFrom: string = createdAtDate?.from ? `from: ${format({ date: createdAtDate.from })}` : "";
  const createdTo: string = createdAtDate?.to ? `to: ${format({ date: createdAtDate.to })}` : "";
  const divider: string = createdFrom && createdTo ? "-" : "";

  const syncFrom: string = syncAtDate?.from ? `from: ${format({ date: syncAtDate.from })}` : "";
  const syncTo: string = syncAtDate?.to ? `to: ${format({ date: syncAtDate.to })}` : "";
  const syncDivider: string = syncFrom && syncTo ? "-" : "";

  const effectiveFrom: string = effectiveAtDate?.from ? `from: ${format({ date: effectiveAtDate.from })}` : "";
  const effectiveTo: string = effectiveAtDate?.to ? `to: ${format({ date: effectiveAtDate.to })}` : "";
  const effectiveDivider: string = effectiveFrom && effectiveTo ? "-" : "";

  const uploadedFrom: string = uploadedAtDate?.from ? `from: ${format({ date: uploadedAtDate.from })}` : "";
  const uploadedTo: string = uploadedAtDate?.to ? `to: ${format({ date: uploadedAtDate.to })}` : "";
  const uploadedDivider: string = uploadedFrom && uploadedTo ? "-" : "";

  return (
    <>
      {Object.entries(multiFilters).map(
        ([key, values]) =>
          Array.isArray(values) &&
          Boolean(values.length) && (
            <StyledChip
              key={key}
              disabled={disabledMultiItems.includes(key as PluralFiltersKeys)}
              label={`${filterNames[key as PluralFiltersKeys]} (${values.length})`}
              onDelete={() => handleDeleteField(key as PluralFiltersKeys)}
              // disabled={Boolean(selectedAccName)}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFilters({
                  key: key as PluralFiltersKeys,
                  anchor: e.currentTarget
                });
              }}
            />
          )
      )}
      {(createdFrom || createdTo) && (
        <StyledChip
          label={`Index Date (${createdFrom} ${divider} ${createdTo})`}
          onDelete={() => setFilters((prevState) => ({ ...prevState, createdAtDate: null }))}
        />
      )}
      {syncAtDate && (
        <StyledChip
          label={`Sync Date (${syncFrom} ${syncDivider} ${syncTo})`}
          onDelete={() => setFilters((prevState) => ({ ...prevState, syncAtDate: null }))}
        />
      )}
      {effectiveAtDate && (
        <StyledChip
          label={`Effective Date (${effectiveFrom} ${effectiveDivider} ${effectiveTo})`}
          onDelete={() => setFilters((prevState) => ({ ...prevState, effectiveAtDate: null }))}
        />
      )}
      {uploadedAtDate && (
        <StyledChip
          label={`Upload Date (${uploadedFrom} ${uploadedDivider} ${uploadedTo})`}
          onDelete={() => setFilters((prevState) => ({ ...prevState, uploadedAtDate: null }))}
        />
      )}
      <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isAdminPermission}>
        {Boolean(synced) && (
          <StyledChip label="Synced" onDelete={() => setFilters((prevState) => ({ ...prevState, synced: false }))} />
        )}
        {Boolean(notSynced) && (
          <StyledChip
            label="Not Synced"
            onDelete={() => setFilters((prevState) => ({ ...prevState, notSynced: false }))}
          />
        )}
      </RoleComponentGuard>
      <Popover
        onClose={() => setSelectedFilters(null)}
        anchorEl={selectedFilters?.anchor}
        open={Boolean(selectedFilters)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        {selectedFilters && (
          <Stack sx={{ gap: "10px", p: "10px", bgcolor: "white", borderRadius: "8px", maxWidth: "300px" }}>
            {filters[selectedFilters.key].map((value) => (
              <StyledChip
                key={value}
                title={value}
                label={value}
                onDelete={() => handleDeleteFieldValue(selectedFilters.key, value)}
              />
            ))}
          </Stack>
        )}
      </Popover>
    </>
  );
};
